<script>
  import { onMount } from "svelte";
  import {
    Page,
    Block,
    BlockTitle,
    Card,
    CardHeader,
    CardContent,
    Row,
    Col,
    Button,
    List,
    ListItem,
    ListInput,
    Preloader,
    f7,
    f7ready,
  } from "framework7-svelte";

  import AdminNavbar from "../components/common/AdminNavbar.svelte";
  import KpiStatCard from "../components/common/KpiStatCard.svelte";
  import StatusBadge from "../components/common/StatusBadge.svelte";

  import { adminQuery, adminMutation, orgMutation } from "../services/adminGQL";
  import {
    calculateDematAccount,
    isValidDemat,
    formatDematDisplay,
    FCML_DP_ID,
    FCML_DP_NAME,
    DEPOSITORY_NAME,
  } from "../utils/dematHelper";
  import {
    isValidIndianMobile,
    normalizeMobile,
    isValidIFSC,
    isValidBankAccount,
    isValidEmail,
    isValidUCC,
  } from "../utils/validationHelper";

  export let f7route;
  export let f7router;

  let activeTab = "approve"; // 'approve' | 'onboard'

  // Pipeline stats
  let analyticsLoading = false;
  let stats = {
    approved: 0,
    pending: 0,
    incomplete: 0,
  };

  // Tab 1: Member Approval State
  let searchMobile = "";
  let isSearchingUser = false;
  let searchedUser = null;
  let searchError = "";

  // Tab 1 editable/calculated fields
  let appFullName = "";
  let appMobile = "";
  let appEmail = "";
  let appUcc = "";
  let appClientId = "";
  let appDematAcc = "";
  let appAccountNo = "";
  let appIfsc = "";
  let appBankName = "";
  let appStatus = "";
  let isApproving = false;

  // Tab 2: Direct Onboarding State
  let obFullName = "";
  let obMobile = "";
  let obEmail = "";
  let obAccountNo = "";
  let obIfsc = "";
  let obBankName = "";
  let obUcc = "";
  let obClientId = "";
  let isOnboarding = false;
  let obCheckCollision = false;
  let obExistingCollision = null;

  // Reactively calculate 16-digit Demat for Tab 1
  $: calculatedAppDemat = calculateDematAccount(appClientId || appDematAcc || appUcc);

  // Reactively calculate 16-digit Demat for Tab 2
  $: calculatedObDemat = calculateDematAccount(obClientId || obUcc);

  // Live validations for Tab 2
  $: isObMobileValid = isValidIndianMobile(obMobile);
  $: isObEmailValid = isValidEmail(obEmail);
  $: isObIfscValid = isValidIFSC(obIfsc);
  $: isObAccountValid = isValidBankAccount(obAccountNo);
  $: isObUccValid = isValidUCC(obUcc);
  $: isObDematValid = isValidDemat(calculatedObDemat);
  $: isObFormValid =
    obFullName.trim().length >= 2 &&
    isObMobileValid &&
    isObEmailValid &&
    isObAccountValid &&
    isObIfscValid &&
    obBankName.trim().length >= 2 &&
    isObUccValid &&
    isObDematValid;

  onMount(() => {
    f7ready(() => {
      fetchAnalytics();
    });
  });

  async function fetchAnalytics() {
    analyticsLoading = true;
    try {
      const res = await adminQuery(
        "user_analytics",
        {},
        "status incomplete pending approved added_money not_added_money"
      );
      if (res && res.data && res.data.user_analytics) {
        stats = {
          approved: res.data.user_analytics.approved || 0,
          pending: res.data.user_analytics.pending || 0,
          incomplete: res.data.user_analytics.incomplete || 0,
        };
      }
    } catch (err) {
      console.warn("Analytics fetch notice:", err);
    } finally {
      analyticsLoading = false;
    }
  }

  // --- TAB 1: Search & Approve ---
  async function handleSearchUser() {
    searchError = "";
    searchedUser = null;
    const cleanedMobile = normalizeMobile(searchMobile);

    if (!isValidIndianMobile(cleanedMobile)) {
      searchError = "Please enter a valid 10-digit Indian mobile number.";
      return;
    }

    isSearchingUser = true;
    try {
      const res = await adminQuery(
        "fetch_all_users",
        { mobile: cleanedMobile },
        "all_users { _id user_id fullName mobile emailAddress ucc trdr_id demat_id demat_acc_number account_number ifsc bank_name account_status approval_status }"
      );

      const users = res?.data?.fetch_all_users?.all_users || [];
      if (!users || users.length === 0) {
        searchError = `No user record found for mobile +91 ${cleanedMobile}.`;
        return;
      }

      const u = users[0];
      searchedUser = u;
      appFullName = u.fullName || "";
      appMobile = u.mobile || cleanedMobile;
      appEmail = u.emailAddress || "";
      appUcc = u.ucc || "";
      appClientId = u.trdr_id || u.demat_id || "";
      appDematAcc = u.demat_acc_number || calculateDematAccount(u.trdr_id || u.demat_id || u.ucc);
      appAccountNo = u.account_number || "";
      appIfsc = u.ifsc || "";
      appBankName = u.bank_name || "";
      appStatus = (u.approval_status || u.account_status || "PENDING").toUpperCase();
    } catch (err) {
      console.error("User search error:", err);
      searchError = "Failed to fetch user: " + (err.message || String(err));
    } finally {
      isSearchingUser = false;
    }
  }

  function confirmApproveMember() {
    if (!searchedUser) return;
    const finalDemat = calculatedAppDemat || appDematAcc;

    if (!isValidDemat(finalDemat)) {
      f7.dialog.alert(
        `Calculated Demat (${finalDemat || "none"}) is not a valid 16-digit account. Please ensure DP Client ID is specified.`,
        "Invalid Demat Account"
      );
      return;
    }

    f7.dialog.create({
      title: "Confirm Member Approval & Demat Provisioning",
      content: `
        <div style="text-align: left; font-size: 13px; line-height: 1.6; padding: 4px 0;">
          <p style="margin: 0 0 8px 0;">Are you sure you want to approve this member and provision CDSL Demat?</p>
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px;">
            <div><strong>Name:</strong> ${appFullName || "-"}</div>
            <div><strong>Mobile:</strong> +91 ${appMobile}</div>
            <div><strong>UCC:</strong> ${appUcc || "-"}</div>
            <div><strong>Demat Account:</strong> <span style="font-family: monospace; color: #16a34a; font-weight: bold;">${formatDematDisplay(finalDemat)}</span></div>
            <div><strong>Depository:</strong> ${DEPOSITORY_NAME} (${FCML_DP_NAME})</div>
            <div><strong>Bank:</strong> ${appBankName || "-"} (${appIfsc || "-"})</div>
          </div>
        </div>
      `,
      buttons: [
        { text: "Cancel", color: "gray" },
        {
          text: "Approve & Provision",
          bold: true,
          color: "green",
          onClick: executeApproveMember,
        },
      ],
    }).open();
  }

  async function executeApproveMember() {
    isApproving = true;
    const finalDemat = calculatedAppDemat || appDematAcc;
    const userId = searchedUser.user_id || searchedUser._id;

    try {
      // 1. Update trdr-org-services (Official member repository and Demat provisioning)
      const orgPayload = {
        user_id: userId,
        mobile: appMobile,
        account_status: "approved",
        approval_status: "approved",
        depository_name: DEPOSITORY_NAME,
        depository_participant: FCML_DP_NAME,
        dp_id: FCML_DP_ID,
        demat_acc_number: finalDemat,
        ucc: appUcc,
        trdr_id: appClientId || finalDemat.slice(-8),
      };

      await orgMutation(
        "update_member_data",
        orgPayload,
        "status _id name mobile approval_status dp_id demat_acc_number ucc"
      );

      // 2. Synchronize trdr-admin-services signup application
      try {
        await adminMutation(
          "update_signup_application",
          {
            user_id: userId,
            account_status: "approved",
            approval_status: "approved",
            demat_acc_number: finalDemat,
            ucc: appUcc,
          },
          "status user_id"
        );
      } catch (adminSyncErr) {
        console.warn("admin-services sync notice:", adminSyncErr);
      }

      appStatus = "APPROVED";
      if (searchedUser) {
        searchedUser.account_status = "approved";
        searchedUser.approval_status = "approved";
        searchedUser.demat_acc_number = finalDemat;
      }

      fetchAnalytics();

      f7.toast.create({
        text: `✓ Successfully approved member & provisioned Demat: ${formatDematDisplay(finalDemat)}`,
        closeTimeout: 4000,
      }).open();
    } catch (err) {
      console.error("Member approval error:", err);
      f7.dialog.alert(err.message || "Failed to approve member. Check backend logs.", "Approval Error");
    } finally {
      isApproving = false;
    }
  }

  // --- TAB 2: Direct FCML Client Onboarding ---
  async function checkObCollision() {
    const cleaned = normalizeMobile(obMobile);
    if (!isValidIndianMobile(cleaned)) return;
    obCheckCollision = true;
    try {
      const res = await adminQuery(
        "fetch_all_users",
        { mobile: cleaned },
        "all_users { _id user_id fullName mobile ucc account_status approval_status }"
      );
      const existing = res?.data?.fetch_all_users?.all_users || [];
      if (existing.length > 0) {
        obExistingCollision = existing[0];
      } else {
        obExistingCollision = null;
      }
    } catch (e) {
      obExistingCollision = null;
    } finally {
      obCheckCollision = false;
    }
  }

  function confirmDirectOnboarding() {
    if (!isObFormValid) {
      f7.dialog.alert("Please correct highlighted validation errors before proceeding.", "Form Incomplete");
      return;
    }

    f7.dialog.create({
      title: "Confirm Direct FCML Client Onboarding",
      content: `
        <div style="text-align: left; font-size: 13px; line-height: 1.6; padding: 4px 0;">
          <p style="margin: 0 0 8px 0;">Dispatching direct client onboarding mutation to <code>trdr-admin-services</code>:</p>
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px;">
            <div><strong>Full Name:</strong> ${obFullName}</div>
            <div><strong>Mobile:</strong> +91 ${normalizeMobile(obMobile)}</div>
            <div><strong>Email:</strong> ${obEmail}</div>
            <div><strong>UCC:</strong> ${obUcc}</div>
            <div><strong>16-Digit Demat:</strong> <span style="font-family: monospace; color: #16a34a; font-weight: bold;">${formatDematDisplay(calculatedObDemat)}</span></div>
            <div><strong>Bank:</strong> ${obBankName} (${obIfsc.toUpperCase()})</div>
            <div><strong>Account No:</strong> ${obAccountNo}</div>
          </div>
          ${obExistingCollision ? '<p style="color: #ea580c; font-size: 12px; margin-top: 8px;">⚠️ Note: This mobile is already registered. It will be updated and set to approved.</p>' : ''}
        </div>
      `,
      buttons: [
        { text: "Cancel", color: "gray" },
        {
          text: "Confirm & Onboard",
          bold: true,
          color: "green",
          onClick: executeDirectOnboarding,
        },
      ],
    }).open();
  }

  async function executeDirectOnboarding() {
    isOnboarding = true;
    try {
      const payload = {
        mobile: normalizeMobile(obMobile),
        ucc: obUcc.trim().toUpperCase(),
        dp_acc_no: calculatedObDemat,
        fullName: obFullName.trim(),
        emailAddress: obEmail.trim(),
        account_number: obAccountNo.trim(),
        ifsc: obIfsc.trim().toUpperCase(),
        bank_name: obBankName.trim(),
        account_status: "approved",
      };

      const res = await adminMutation(
        "add_approve_user_by_admin",
        payload,
        "status message"
      );

      const msg = res?.data?.add_approve_user_by_admin?.message || "Client successfully onboarded and approved!";

      // Also ensure depository settings are synchronized on org-services
      try {
        await orgMutation(
          "update_member_data",
          {
            mobile: payload.mobile,
            account_status: "approved",
            approval_status: "approved",
            depository_name: DEPOSITORY_NAME,
            depository_participant: FCML_DP_NAME,
            dp_id: FCML_DP_ID,
            demat_acc_number: calculatedObDemat,
            ucc: payload.ucc,
          },
          "status _id mobile approval_status"
        );
      } catch (orgErr) {
        console.warn("Org services sync notice:", orgErr);
      }

      fetchAnalytics();

      f7.dialog.alert(
        `<div style="text-align: center;">
          <div style="font-size: 32px; margin-bottom: 8px;">🎉</div>
          <p><strong>${msg}</strong></p>
          <p style="font-size: 12px; color: #64748b;">Member: ${obFullName} | UCC: ${obUcc} | Demat: ${formatDematDisplay(calculatedObDemat)}</p>
        </div>`,
        "Onboarding Successful",
        () => {
          // Reset onboarding form
          obFullName = "";
          obMobile = "";
          obEmail = "";
          obAccountNo = "";
          obIfsc = "";
          obBankName = "";
          obUcc = "";
          obClientId = "";
          obExistingCollision = null;
        }
      );
    } catch (err) {
      console.error("Direct onboarding error:", err);
      f7.dialog.alert(err.message || "Failed to onboard client.", "Onboarding Error");
    } finally {
      isOnboarding = false;
    }
  }
</script>

<Page class="approve-account-page">
  <AdminNavbar title="Account Approval & Onboarding" subtitle="Task 2 & 3: Demat Provisioning & FCML Client Setup" />

  <div class="page-content-wrapper padding">
    <!-- Pipeline Metrics Row -->
    <Row class="margin-bottom">
      <Col width="100" medium="33">
        <KpiStatCard
          title="Approved Members"
          value={stats.approved}
          subtitle="Full Demat & KYC Active"
          icon="f7:checkmark_circle_fill"
          iconColor="#10b981"
          theme="success"
        />
      </Col>
      <Col width="100" medium="33">
        <KpiStatCard
          title="Pending Approval"
          value={stats.pending}
          subtitle="Awaiting Demat Allocation"
          icon="f7:hourglass"
          iconColor="#f59e0b"
          theme="warning"
        />
      </Col>
      <Col width="100" medium="33">
        <KpiStatCard
          title="Incomplete Pipeline"
          value={stats.incomplete}
          subtitle="Registration In Progress"
          icon="f7:person_crop_circle_badge_exclam"
          iconColor="#64748b"
          theme="default"
        />
      </Col>
    </Row>

    <!-- Operational Tabs Segmented Switcher -->
    <div class="tabs-segment-wrapper margin-vertical">
      <div class="segmented segmented-raised">
        <Button
          active={activeTab === "approve"}
          on:click={() => (activeTab = "approve")}
          class="tab-btn"
        >
          <span class="tab-btn-icon">👤</span>
          <span>1. Member Approval & Demat Provisioning (Task 2)</span>
        </Button>
        <Button
          active={activeTab === "onboard"}
          on:click={() => (activeTab = "onboard")}
          class="tab-btn"
        >
          <span class="tab-btn-icon">⚡</span>
          <span>2. Direct FCML Client Onboarding (Task 3)</span>
        </Button>
      </div>
    </div>

    <!-- TAB 1: Member Approval & Demat Provisioning -->
    {#if activeTab === "approve"}
      <Card class="op-card">
        <CardHeader class="op-card-header">
          <div class="card-header-title">
            <span class="card-header-badge">Task 2</span>
            <span>Search & Approve Registered Member</span>
          </div>
          <div class="card-header-note">Calls <code>update_member_data</code> on <code>trdr-org-services</code></div>
        </CardHeader>

        <CardContent>
          <!-- Search Row -->
          <div class="search-section margin-bottom">
            <div class="search-input-group">
              <span class="search-prefix">+91</span>
              <input
                type="tel"
                placeholder="Enter 10-digit Mobile Number"
                bind:value={searchMobile}
                on:keydown={(e) => e.key === "Enter" && handleSearchUser()}
                class="search-text-input"
                maxlength="10"
              />
              <Button
                fill
                color="blue"
                on:click={handleSearchUser}
                disabled={isSearchingUser || !searchMobile}
                class="search-action-btn"
              >
                {#if isSearchingUser}
                  <Preloader size={16} color="white" />
                  <span>Searching...</span>
                {:else}
                  <span>Search Member</span>
                {/if}
              </Button>
            </div>

            {#if searchError}
              <div class="search-error-banner margin-top-half">
                ⚠️ {searchError}
              </div>
            {/if}
          </div>

          <!-- Searched Member Profile Details -->
          {#if searchedUser}
            <div class="member-profile-grid margin-top">
              <div class="profile-header-banner">
                <div class="profile-main-info">
                  <h3 class="profile-name">{appFullName || "Unnamed Member"}</h3>
                  <span class="profile-meta">+91 {appMobile} • {appEmail || "No email"}</span>
                </div>
                <div class="profile-status-block">
                  <StatusBadge status={appStatus} size="large" />
                </div>
              </div>

              <!-- Form Fields (Fixed bindings, no copypasta) -->
              <div class="fields-two-col margin-top">
                <div class="field-item">
                  <label>Full Name</label>
                  <input type="text" bind:value={appFullName} class="form-input" placeholder="Full Name" />
                </div>

                <div class="field-item">
                  <label>Mobile Number</label>
                  <input type="tel" bind:value={appMobile} class="form-input" placeholder="10-digit Mobile" readonly />
                </div>

                <div class="field-item">
                  <label>Email Address</label>
                  <input type="email" bind:value={appEmail} class="form-input" placeholder="Email Address" />
                </div>

                <div class="field-item">
                  <label>Unique Client Code (UCC)</label>
                  <input type="text" bind:value={appUcc} class="form-input" placeholder="e.g. TRDR001" />
                </div>

                <div class="field-item">
                  <label>DP Client ID (8-digit Beneficiary ID)</label>
                  <input
                    type="text"
                    bind:value={appClientId}
                    class="form-input"
                    placeholder="e.g. 00012345"
                    maxlength="8"
                  />
                  <span class="field-hint">Used to calculate 16-digit CDSL Demat Account</span>
                </div>

                <div class="field-item">
                  <label>16-Digit CDSL Demat Account Number</label>
                  <div class="demat-display-box">
                    <span class="demat-dp-part">{FCML_DP_ID}</span>
                    <span class="demat-client-part">{calculatedAppDemat.slice(8) || "00000000"}</span>
                    {#if isValidDemat(calculatedAppDemat)}
                      <span class="demat-badge valid">✓ Valid 16-Digit CDSL</span>
                    {:else}
                      <span class="demat-badge invalid">⚠️ Needs 8-digit Client ID</span>
                    {/if}
                  </div>
                </div>

                <div class="field-item">
                  <label>Bank Account Number</label>
                  <input type="text" bind:value={appAccountNo} class="form-input" placeholder="Bank Account Number" />
                </div>

                <div class="field-item">
                  <label>Bank IFSC Code</label>
                  <input type="text" bind:value={appIfsc} class="form-input" placeholder="e.g. HDFC0001234" />
                </div>

                <div class="field-item full-width">
                  <label>Bank Name</label>
                  <input type="text" bind:value={appBankName} class="form-input" placeholder="e.g. HDFC Bank Ltd" />
                </div>
              </div>

              <!-- Action Bar -->
              <div class="approval-action-bar margin-top">
                <Button
                  fill
                  large
                  color="green"
                  on:click={confirmApproveMember}
                  disabled={isApproving || appStatus === "APPROVED"}
                  class="action-submit-btn"
                >
                  {#if isApproving}
                    <Preloader size={20} color="white" />
                    <span>Provisioning Demat & Approving...</span>
                  {:else if appStatus === "APPROVED"}
                    <span>✓ Member Already Approved</span>
                  {:else}
                    <span>Approve Member & Provision Demat Account</span>
                  {/if}
                </Button>
              </div>
            </div>
          {:else if !searchError && !isSearchingUser}
            <div class="empty-state-notice">
              <span class="empty-state-icon">🔍</span>
              <p>Enter a 10-digit mobile number above to look up member records and provision Demat accounts.</p>
            </div>
          {/if}
        </CardContent>
      </Card>
    {/if}

    <!-- TAB 2: Direct FCML Client Onboarding -->
    {#if activeTab === "onboard"}
      <Card class="op-card">
        <CardHeader class="op-card-header">
          <div class="card-header-title">
            <span class="card-header-badge">Task 3</span>
            <span>Direct FCML Client Onboarding & Instant Provisioning</span>
          </div>
          <div class="card-header-note">Dispatches <code>add_approve_user_by_admin</code> to <code>trdr-admin-services</code></div>
        </CardHeader>

        <CardContent>
          <div class="onboarding-form-grid">
            <!-- Full Name -->
            <div class="field-item">
              <label>Full Name <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obFullName}
                class="form-input"
                class:has-error={obFullName && obFullName.trim().length < 2}
                placeholder="Full Legal Name"
              />
            </div>

            <!-- Mobile -->
            <div class="field-item">
              <label>10-Digit Mobile Number <span class="req">*</span></label>
              <input
                type="tel"
                bind:value={obMobile}
                on:blur={checkObCollision}
                class="form-input"
                class:has-error={obMobile && !isObMobileValid}
                placeholder="e.g. 9876543210"
                maxlength="10"
              />
              {#if obMobile && !isObMobileValid}
                <span class="field-error">Enter a valid 10-digit Indian mobile number</span>
              {/if}
              {#if obExistingCollision}
                <span class="field-warning">⚠️ Existing member found: {obExistingCollision.fullName || obExistingCollision.mobile} ({obExistingCollision.account_status})</span>
              {/if}
            </div>

            <!-- Email -->
            <div class="field-item">
              <label>Email Address <span class="req">*</span></label>
              <input
                type="email"
                bind:value={obEmail}
                class="form-input"
                class:has-error={obEmail && !isObEmailValid}
                placeholder="client@example.com"
              />
              {#if obEmail && !isObEmailValid}
                <span class="field-error">Enter a valid email address</span>
              {/if}
            </div>

            <!-- UCC -->
            <div class="field-item">
              <label>Unique Client Code (UCC) <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obUcc}
                class="form-input"
                class:has-error={obUcc && !isObUccValid}
                placeholder="e.g. 23_FEDE"
              />
              {#if obUcc && !isObUccValid}
                <span class="field-error">Enter a valid UCC (3-12 alphanumeric characters)</span>
              {/if}
            </div>

            <!-- DP Client ID -->
            <div class="field-item">
              <label>DP Client ID (8 digits) <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obClientId}
                class="form-input"
                placeholder="e.g. 00012345"
                maxlength="8"
              />
              <span class="field-hint">Auto-padded to 8 digits with leading zeros</span>
            </div>

            <!-- 16-Digit Calculated Demat Preview -->
            <div class="field-item">
              <label>Calculated 16-Digit CDSL Demat Account</label>
              <div class="demat-display-box">
                <span class="demat-dp-part">{FCML_DP_ID}</span>
                <span class="demat-client-part">{calculatedObDemat.slice(8) || "00000000"}</span>
                {#if isObDematValid}
                  <span class="demat-badge valid">✓ Valid 16-Digit</span>
                {:else}
                  <span class="demat-badge invalid">⚠️ Incomplete</span>
                {/if}
              </div>
            </div>

            <!-- Bank Account Number -->
            <div class="field-item">
              <label>Bank Account Number <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obAccountNo}
                class="form-input"
                class:has-error={obAccountNo && !isObAccountValid}
                placeholder="9 to 18 digits"
              />
              {#if obAccountNo && !isObAccountValid}
                <span class="field-error">Enter a valid bank account number (9-18 digits)</span>
              {/if}
            </div>

            <!-- Bank IFSC -->
            <div class="field-item">
              <label>Bank IFSC Code <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obIfsc}
                class="form-input"
                class:has-error={obIfsc && !isObIfscValid}
                placeholder="e.g. HDFC0001234"
                maxlength="11"
              />
              {#if obIfsc && !isObIfscValid}
                <span class="field-error">Enter valid 11-character RBI IFSC (e.g. SBIN0001234)</span>
              {/if}
            </div>

            <!-- Bank Name -->
            <div class="field-item full-width">
              <label>Bank Name <span class="req">*</span></label>
              <input
                type="text"
                bind:value={obBankName}
                class="form-input"
                class:has-error={obBankName && obBankName.trim().length < 2}
                placeholder="Bank Name (e.g. State Bank of India)"
              />
            </div>
          </div>

          <!-- Direct Onboarding Action -->
          <div class="approval-action-bar margin-top">
            <Button
              fill
              large
              color="blue"
              on:click={confirmDirectOnboarding}
              disabled={isOnboarding || !isObFormValid}
              class="action-submit-btn"
            >
              {#if isOnboarding}
                <Preloader size={20} color="white" />
                <span>Onboarding Client...</span>
              {:else}
                <span>Submit FCML Client Onboarding & Approve</span>
              {/if}
            </Button>
          </div>
        </CardContent>
      </Card>
    {/if}
  </div>
</Page>

<style>
  .page-content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
  }

  .tabs-segment-wrapper {
    background: #ffffff;
    padding: 6px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }

  .tab-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 600;
    text-transform: none;
    font-size: 14px;
    padding: 10px 16px;
    height: auto;
  }

  .tab-btn-icon {
    font-size: 16px;
  }

  .op-card {
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .op-card-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 18px;
  }

  .card-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .card-header-badge {
    background: #2563eb;
    color: white;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
  }

  .card-header-note {
    font-size: 12px;
    color: #64748b;
  }

  .search-section {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px;
  }

  .search-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .search-prefix {
    font-weight: 700;
    color: #475569;
    font-size: 14px;
  }

  .search-text-input {
    flex: 1;
    height: 42px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 15px;
    background: #ffffff;
    color: #0f172a;
  }

  .search-action-btn {
    height: 42px;
    padding: 0 20px;
    font-weight: 600;
    text-transform: none;
    border-radius: 8px;
  }

  .search-error-banner {
    color: #b91c1c;
    background: #fee2e2;
    border: 1px solid #fca5a5;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
  }

  .profile-header-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 18px;
  }

  .profile-name {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .profile-meta {
    font-size: 13px;
    color: #64748b;
  }

  .fields-two-col,
  .onboarding-form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    .fields-two-col,
    .onboarding-form-grid {
      grid-template-columns: 1fr;
    }
  }

  .field-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .field-item.full-width {
    grid-column: 1 / -1;
  }

  .field-item label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .req {
    color: #ef4444;
  }

  .form-input {
    height: 40px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 14px;
    color: #0f172a;
    background: #ffffff;
    box-sizing: border-box;
  }

  .form-input:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  .form-input.has-error {
    border-color: #ef4444;
    background: #fef2f2;
  }

  .field-hint {
    font-size: 11px;
    color: #64748b;
  }

  .field-error {
    font-size: 11px;
    color: #b91c1c;
    font-weight: 500;
  }

  .field-warning {
    font-size: 11px;
    color: #c2410c;
    font-weight: 500;
  }

  .demat-display-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    border: 1px solid #94a3b8;
    background: #f8fafc;
    border-radius: 8px;
    padding: 0 12px;
    font-family: monospace;
    font-size: 15px;
    box-sizing: border-box;
  }

  .demat-dp-part {
    color: #2563eb;
    font-weight: 700;
  }

  .demat-client-part {
    color: #0f172a;
    font-weight: 700;
  }

  .demat-badge {
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: sans-serif;
  }

  .demat-badge.valid {
    background: #dcfce7;
    color: #15803d;
  }

  .demat-badge.invalid {
    background: #fee2e2;
    color: #b91c1c;
  }

  .approval-action-bar {
    padding-top: 10px;
    border-top: 1px solid #e2e8f0;
  }

  .action-submit-btn {
    border-radius: 8px;
    font-weight: 700;
    text-transform: none;
    font-size: 15px;
  }

  .empty-state-notice {
    text-align: center;
    padding: 40px 20px;
    color: #64748b;
  }

  .empty-state-icon {
    font-size: 36px;
    display: block;
    margin-bottom: 8px;
  }
</style>
